import { useReducer, useRef } from "react";
import useUserBasedTempFileService from "@app-contexts/user/features/temp-files/data/services/useUserBasedTempFileService";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import { toBase64 } from "@shared/utils/file-utils/utils";
import { UserBasedTempFileSerializer } from "@app-contexts/user/features/temp-files/data/types/temp-file-types";
import { ImageInsertData } from "./ImageInsertModal.props";

interface ImageInsertState {
  src: string;
  alt: string;
  width: number | undefined;
  height: number | undefined;
  constrainProportions: boolean;
  originalAspectRatio: number | null;
  isUploading: boolean;
}

enum ImageInsertActionType {
  RESET = "RESET",
  SET_SRC = "SET_SRC",
  SET_ALT = "SET_ALT",
  SET_WIDTH = "SET_WIDTH",
  SET_HEIGHT = "SET_HEIGHT",
  SET_CONSTRAIN_PROPORTIONS = "SET_CONSTRAIN_PROPORTIONS",
  SET_UPLOADING = "SET_UPLOADING",
  UPLOAD_SUCCESS = "UPLOAD_SUCCESS",
  UPDATE_WIDTH_WITH_ASPECT_RATIO = "UPDATE_WIDTH_WITH_ASPECT_RATIO",
  UPDATE_HEIGHT_WITH_ASPECT_RATIO = "UPDATE_HEIGHT_WITH_ASPECT_RATIO",
}

type ImageInsertAction =
  | { type: ImageInsertActionType.RESET }
  | { type: ImageInsertActionType.SET_SRC; payload: ImageInsertState["src"] }
  | { type: ImageInsertActionType.SET_ALT; payload: ImageInsertState["alt"] }
  | {
      type: ImageInsertActionType.SET_WIDTH;
      payload: ImageInsertState["width"];
    }
  | {
      type: ImageInsertActionType.SET_HEIGHT;
      payload: ImageInsertState["height"];
    }
  | {
      type: ImageInsertActionType.SET_CONSTRAIN_PROPORTIONS;
      payload: ImageInsertState["constrainProportions"];
    }
  | {
      type: ImageInsertActionType.SET_UPLOADING;
      payload: ImageInsertState["isUploading"];
    }
  | {
      type: ImageInsertActionType.UPLOAD_SUCCESS;
      payload: {
        src: ImageInsertState["src"];
        width: ImageInsertState["width"];
        height: ImageInsertState["height"];
        aspectRatio: number;
      };
    }
  | {
      type: ImageInsertActionType.UPDATE_WIDTH_WITH_ASPECT_RATIO;
      payload: { width: number; aspectRatio: number };
    }
  | {
      type: ImageInsertActionType.UPDATE_HEIGHT_WITH_ASPECT_RATIO;
      payload: { height: number; aspectRatio: number };
    };

const initialState: ImageInsertState = {
  src: "",
  alt: "",
  width: undefined,
  height: undefined,
  constrainProportions: true,
  originalAspectRatio: null,
  isUploading: false,
};

function imageInsertReducer(
  state: ImageInsertState,
  action: ImageInsertAction
): ImageInsertState {
  switch (action.type) {
    case ImageInsertActionType.RESET:
      return initialState;

    case ImageInsertActionType.SET_SRC:
      return { ...state, src: action.payload };

    case ImageInsertActionType.SET_ALT:
      return { ...state, alt: action.payload };

    case ImageInsertActionType.SET_WIDTH:
      return { ...state, width: action.payload };

    case ImageInsertActionType.SET_HEIGHT:
      return { ...state, height: action.payload };

    case ImageInsertActionType.SET_CONSTRAIN_PROPORTIONS:
      return { ...state, constrainProportions: action.payload };

    case ImageInsertActionType.SET_UPLOADING:
      return { ...state, isUploading: action.payload };

    case ImageInsertActionType.UPLOAD_SUCCESS:
      return {
        ...state,
        src: action.payload.src,
        width: action.payload.width,
        height: action.payload.height,
        originalAspectRatio: action.payload.aspectRatio,
        isUploading: false,
      };

    case ImageInsertActionType.UPDATE_WIDTH_WITH_ASPECT_RATIO:
      return {
        ...state,
        width: action.payload.width,
        height: Math.round(action.payload.width / action.payload.aspectRatio),
      };

    case ImageInsertActionType.UPDATE_HEIGHT_WITH_ASPECT_RATIO:
      return {
        ...state,
        height: action.payload.height,
        width: Math.round(action.payload.height * action.payload.aspectRatio),
      };

    default:
      return state;
  }
}

export default function useImageInsertModal() {
  const [state, dispatch] = useReducer(imageInsertReducer, initialState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { useCreate } = useUserBasedTempFileService();
  const tempFileCreateMutation = useCreate();
  const notifier = NotifierFactory.createNotifier();

  const handleReset = () => {
    dispatch({ type: ImageInsertActionType.RESET });
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      dispatch({ type: ImageInsertActionType.SET_UPLOADING, payload: true });

      try {
        // Convert file to base64
        const base64String = await toBase64(file);

        // Calculate expires_at to 5 years from now
        const fiveYearsFromNow = new Date();
        fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);
        const expiresAt = fiveYearsFromNow.toISOString();

        // Upload file to backend using the required payload format
        const response = (await tempFileCreateMutation.mutateAsync({
          expires_at: expiresAt,
          file: base64String as string,
        })) as UserBasedTempFileSerializer;

        // Get image dimensions for aspect ratio
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.naturalWidth / img.naturalHeight;
          dispatch({
            type: ImageInsertActionType.UPLOAD_SUCCESS,
            payload: {
              src: response.file,
              width: img.naturalWidth,
              height: img.naturalHeight,
              aspectRatio,
            },
          });
        };
        img.src = response.file;
      } catch (error) {
        notifier.error(getExceptionToastDescription(error));
        dispatch({ type: ImageInsertActionType.SET_UPLOADING, payload: false });
      }
    }
  };

  const handleWidthChange = (newWidth: number) => {
    if (state.constrainProportions && state.originalAspectRatio) {
      dispatch({
        type: ImageInsertActionType.UPDATE_WIDTH_WITH_ASPECT_RATIO,
        payload: { width: newWidth, aspectRatio: state.originalAspectRatio },
      });
    } else {
      dispatch({ type: ImageInsertActionType.SET_WIDTH, payload: newWidth });
    }
  };

  const handleHeightChange = (newHeight: number) => {
    if (state.constrainProportions && state.originalAspectRatio) {
      dispatch({
        type: ImageInsertActionType.UPDATE_HEIGHT_WITH_ASPECT_RATIO,
        payload: { height: newHeight, aspectRatio: state.originalAspectRatio },
      });
    } else {
      dispatch({ type: ImageInsertActionType.SET_HEIGHT, payload: newHeight });
    }
  };

  const handleSubmit = (
    onInsert: (imageData: ImageInsertData) => void,
    onClose: () => void
  ) => {
    if (!state.src.trim()) return;

    onInsert({
      src: state.src.trim(),
      alt: state.alt.trim(),
      width: state.width,
      height: state.height,
    });

    handleReset();
    onClose();
  };

  const handleCancel = (onClose: () => void) => {
    handleReset();
    onClose();
  };

  return {
    // State
    src: state.src,
    setSrc: (value: string) =>
      dispatch({ type: ImageInsertActionType.SET_SRC, payload: value }),
    alt: state.alt,
    setAlt: (value: string) =>
      dispatch({ type: ImageInsertActionType.SET_ALT, payload: value }),
    width: state.width,
    height: state.height,
    constrainProportions: state.constrainProportions,
    setConstrainProportions: (value: boolean) =>
      dispatch({
        type: ImageInsertActionType.SET_CONSTRAIN_PROPORTIONS,
        payload: value,
      }),
    isUploading: state.isUploading,
    fileInputRef,

    // Handlers
    handleFileSelect,
    handleFileChange,
    handleWidthChange,
    handleHeightChange,
    handleSubmit,
    handleCancel,
  };
}
